// @flow
import * as React from 'react'
import { withRouter } from 'react-router-dom'
import Button from '@material-ui/core/Button'
import { MuiThemeProvider } from '@material-ui/core/styles'
import Switch from '@material-ui/core/Switch'
import muiTheme from 'catena/er2_styles/mui'
import CollaborationSettings from './collaboration_settings'
import * as ids from '../constants/ids'
import * as errors from '../constants/errors'
import ErrorInline from './error_inline'
import { CollaborationType } from '../reducers/collaboration'
import stylesFicus from '../styles/ficus_style'
import styles from '../styles'
import * as colors from '../styles/colors'

type Props = {
    collaboration: CollaborationType,
    collaborationSettings: CollaborationSettingsType,
    fetchSessions: Function,
    fetchSessionState: Function,
    location: { pathname: string, search?: string },
    onChangeCollaborationInput: Function,
    onJoinSession: Function,
    onLeaveSession: Function,
    onShareSession: Function,
    onUnshareSession: Function,
    theme: styles,
    token: { value: string, expires: number },
    updateCollaborationSettings: Function,
    updateSettings: Function,
    visible: boolean,
}

/**
 * Determines if the server has retrieved sessions from the server.
 */
export function sessionsAreLoaded(props: Props) {
    return props.collaboration.sessions.items
}

/**
 * Determines if any sessions are available to be subscribed to.
 */
export function hasSessions(props: Props) {
    return sessionsAreLoaded(props) && props.collaboration.sessions.items.length > 0
}

/**
 * Determines if the current user has shared their session with others.
 */
export function iSharedSession(props: Props) {
    return hasSessions(props) && props.collaboration.sessions.items[0].token === props.token.value
}

/**
 * Determines if the another user has shared their session with others.
 */
export function anotherHasShared(props: Props) {
    return hasSessions(props) && !iSharedSession(props)
}

/**
 * Determines if the another user has shared their session with others.
 */
export function someoneHasShared(props: Props) {
    return iSharedSession(props) || anotherHasShared(props)
}

/* eslint-disable react/prefer-stateless-function */
class CollaborationPanel extends React.Component<Props> {
    constructor(props) {
        super(props)
        // Help
        this.state = {
            readOnly: false,
            share: true,
        }
    }
    componentDidMount() {
        this.props.fetchSessions()
    }

    renderShareButton() {
        const sessionsLoaded = sessionsAreLoaded(this.props)
        const iSharedMemo = iSharedSession(this.props)
        const anotherHasSharedMemo = anotherHasShared(this.props)
        const someoneHasSharedMemo = someoneHasShared(this.props)
        const haveJoined = this.props.collaboration.enabled
        const fetchError = this.props.collaboration.fetchErrors.joinSession
        const validationError = this.props.collaboration.form.validationErrors.sessionName
            && this.props.collaboration.form.validationErrors.sessionName.type !== errors.NO_ERROR
        if (!sessionsLoaded) {
            return (
                <div style={{ textAlign: 'center' }}>
                    Loading Sessions...
                </div>
            )
        }

        // Update unload events
        window.onbeforeunload = () => {
            if (iSharedMemo) {
                this.props.onUnshareSession()
            } else if (haveJoined) {
                // this.props.onLeaveSession()
            }
        }

        return (
            <div className={`${this.props.theme.collaborationLabel} ${this.props.theme.styleLabelPlusToggle}`}>
                {fetchError &&
                <div className={`${this.props.theme.error} ${this.props.theme.errorInlineCtr}`}>
                    <div className={this.props.theme.errorInline}>
                        { this.props.collaboration.fetchErrors.joinSession.message }
                    </div>
                </div>
                }
                {iSharedMemo &&
                <div className={this.props.theme.section} style={{ textAlign: 'center', marginTop: '6px' }}>
                    <div style={{ textAlign: 'left' }}>You are sharing <b>{this.props.collaboration.sessions.items[0].name}</b></div>
                    <div style={{ marginTop: '16px' }}>
                        <Button
                            variant="contained"
                            onClick={this.props.onUnshareSession}
                        >
                            Unshare
                        </Button>
                    </div>
                </div>
                }
                {anotherHasSharedMemo && !haveJoined &&
                <div className={this.props.theme.section}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ textAlign: 'left', marginTop: '6px', marginBottom: '6px' }}>Session available: <b>{this.props.collaboration.sessions.items[0].name}</b></div>
                        <Button
                            variant="contained"
                            onClick={() => this.props.onJoinSession(this.props.collaboration.sessions.items[0].token)}
                        >
                            Join
                        </Button>
                    </div>
                </div>
                }
                {anotherHasSharedMemo && haveJoined &&
                <div className={this.props.theme.section} style={{ textAlign: 'center' }}>
                    <div style={{ marginTop: '6px', marginBottom: '6px', textAlign: 'left' }}>You joined <b>{ this.props.collaboration.sessions.items[0].name }</b></div>
                    <Button
                        variant={'contained'}
                        onClick={() => this.props.onLeaveSession(this.props.collaboration.sharedState.token)}
                    >
                        Leave
                    </Button>
                </div>
                }
                {!someoneHasSharedMemo &&
                <div
                    className={`${this.props.theme.section} ${validationError ? this.props.theme.error : ''}`}
                >
                    <div className={this.props.theme.formGroup}>
                        <input
                            className={this.props.theme.sessionNameInput}
                            id={ids.COLLABORATION_SESSION_NAME}
                            onChange={evt => this.props.onChangeCollaborationInput(
                                ids.COLLABORATION_SESSION_NAME,
                                evt.target.value,
                            )}
                            placeholder="Enter a session name ..."
                            value={this.props.collaboration.form.sessionName}
                        />
                        {validationError &&
                            <i
                                className={`${this.props.theme.error} fa fa-exclamation-triangle`}
                                style={{ marginLeft: '5px' }}
                            />
                        }
                    </div>
                    {validationError &&
                    <ErrorInline
                        theme={this.props.theme}
                        error={{
                            name: 'Session Name',
                            type: this.props.collaboration.form.validationErrors.sessionName.type,
                        }}
                    />
                    }
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                        <Button
                            variant="contained"
                            onClick={this.props.onShareSession}
                        >
                            Share
                        </Button>
                    </div>
                </div>
                }
            </div>
        )
    }

    createSharingEmail() {
        const email = prompt('Enter Email Address')
        if (email) {
            const subject = 'Join my session'
            const body = `${window.location.origin}`
            const win = window.open(`mailto:${email}?subject=${subject}&body=${body}`)
            win.close()
        }
    }

    render() {
        const button = this.renderShareButton()
        const theMuiTheme = muiTheme(colors)
        return (
            <MuiThemeProvider theme={theMuiTheme}>
                <div className={`${this.props.theme.collaboration} ${!this.props.visible ? this.props.theme.hidden : ''}`}>
                    <div className={this.props.theme.section} style={{ marginBottom: '10px' }}>
                        <span className={this.props.theme.headerTitle}>Share/Join</span>
                        {button}
                        <span
                            className={`fa fa-question-circle-o ${this.props.theme.helpIcon}`}
                            tabIndex={-1}
                            onClick={() => this.setState({ ...this.state, share: !this.state.share })}
                            role="button"
                        />
                        {this.state.share && (
                            <div className={`${this.props.theme.section} ${stylesFicus.help}`}>
                                Brushing and Linking is used to share FICUS map interaction and location information among a group of windows. Those
                                windows are linked together in a session.
                                All session members will be able to watch map changes and analytics made in real-time. <br /><br />
                                Step 1: Initiate brushing by entering a name for a session to share.<br />
                                Step 2: In a different browser window, link to the shared session by joining it.<br />
                                <br />
                                Each window can also choose in detail which changes to follow. It can also operate in observer mode where no local interactions are shared with others.<br />
                            </div>
                        )}
                        <div className={this.props.theme.section}>
                            <span className={this.props.theme.collaborationLabel}>Observer Mode</span>
                            <Switch
                                checked={this.props.collaborationSettings.readOnly}
                                color="secondary"
                                onChange={e => this.props.updateCollaborationSettings({ readOnly: !this.props.collaborationSettings.readOnly })}
                            />
                            <span
                                className={`fa fa-question-circle-o ${this.props.theme.helpIcon}`}
                                onClick={() => this.setState({ readOnly: !this.state.readOnly })}
                                role="button"
                                tabIndex={-1}
                            />
                            {this.state.readOnly && (
                                <div className={this.props.theme.help}>Any changes (map extends, analysis, location statistics, ...) you make locally will not be seen by others in the same session.</div>
                            )}
                        </div>
                    </div>

                    <div className={this.props.theme.section}>
                        <span className={this.props.theme.ficusControlsHeaderTitle} style={{ display: 'inline-block', marginBottom: '14px' }}>Synchronize with other Views</span>
                        <CollaborationSettings
                            settings={this.props.collaborationSettings}
                            theme={this.props.theme}
                            updateSettings={this.props.updateCollaborationSettings}
                        />
                    </div>
                </div>
            </MuiThemeProvider>
        )
    }
}

export default withRouter(CollaborationPanel)
